/**
 * Página de capítulo. Lee el MD desde `chapterMarkdown` según el slug
 * de la URL y lo renderiza con react-markdown + GFM (tablas, listas
 * de tareas). Al final pinta procesos/documentos/vídeos relacionados
 * definidos en el índice del capítulo.
 */

import { Alert, Divider, Typography } from "antd";
import { useParams } from "react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PageHeader } from "@/components/common/PageHeader";
import { onboardingMarkdownComponents } from "./markdownComponents";
import { usePageTitle } from "@/hooks";
import {
  chaptersBySlug,
  documentsBySlug,
  processesBySlug,
  videosBySlug,
} from "@/data/onboarding";
import { chapterMarkdown } from "@/data/onboarding/chapterContent";
import { ProcessCard } from "./ProcessCard";
import { DocumentCard } from "./DocumentCard";
import { VideoEmbed } from "./VideoEmbed";

const { Title } = Typography;

export const ChapterPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const chapter = slug ? chaptersBySlug.get(slug) : undefined;

  usePageTitle(chapter?.label ?? "Capítulo");

  if (!chapter) {
    return (
      <Alert
        type="warning"
        message="Capítulo no encontrado"
        description={`No hay ningún capítulo con el slug "${slug}".`}
      />
    );
  }

  const markdown = chapterMarkdown[chapter.contentSlug] ?? "";

  const processes = (chapter.processSlugs ?? [])
    .map((s) => processesBySlug.get(s))
    .filter((p): p is NonNullable<typeof p> => !!p);
  const documents = (chapter.documentSlugs ?? [])
    .map((s) => documentsBySlug.get(s))
    .filter((d): d is NonNullable<typeof d> => !!d);
  const videos = (chapter.videoSlugs ?? [])
    .map((s) => videosBySlug.get(s))
    .filter((v): v is NonNullable<typeof v> => !!v);

  return (
    <>
      <PageHeader title={chapter.title} subtitle={chapter.summary} />

      <div className="onboarding-markdown">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={onboardingMarkdownComponents}
        >
          {markdown}
        </ReactMarkdown>
      </div>

      {processes.length > 0 ? (
        <>
          <Divider />
          <Title level={3}>Procesos relacionados</Title>
          {processes.map((p) => (
            <ProcessCard key={p.slug} process={p} />
          ))}
        </>
      ) : null}

      {documents.length > 0 ? (
        <>
          <Divider />
          <Title level={3}>Documentos del capítulo</Title>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 12,
            }}
          >
            {documents.map((d) => (
              <DocumentCard key={d.slug} doc={d} />
            ))}
          </div>
        </>
      ) : null}

      {videos.length > 0 ? (
        <>
          <Divider />
          <Title level={3}>Vídeos del capítulo</Title>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: 12,
            }}
          >
            {videos.map((v) => (
              <VideoEmbed key={v.slug} video={v} />
            ))}
          </div>
        </>
      ) : null}
    </>
  );
};
